import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Crown, Check, Clock, CreditCard, X, AlertTriangle, Tag, Loader2, Gift, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { usePlatform } from '../hooks/usePlatform';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Subscription = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  const { hideWebPurchaseUI } = usePlatform();
  const { 
    subscription, 
    isPremium, 
    isTrialing, 
    trialDaysLeft,
    startTrial, 
    subscribe, 
    cancelSubscription,
    loading: subLoading 
  } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [validatedCode, setValidatedCode] = useState(null);
  const [validatingCode, setValidatingCode] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('monthly'); // 'monthly' | 'yearly'

  const validateDiscountCode = async (code) => {
    if (!code || code.length < 3) {
      setValidatedCode(null);
      return;
    }

    setValidatingCode(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/discount/validate`, { code: code.toUpperCase() }, { headers });
      setValidatedCode(response.data);
      toast.success(response.data.description + ' toegepast!');
    } catch (error) {
      setValidatedCode(null);
      toast.error(error.response?.data?.detail || 'Ongeldige code');
    } finally {
      setValidatingCode(false);
    }
  };

  const handleStartTrial = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setLoading(true);
    const result = await startTrial();
    setLoading(false);

    if (result.success) {
      toast.success(`🎉 Proefperiode gestart! Je hebt ${result.data.days} dagen gratis toegang.`);
    } else {
      toast.error(result.error);
    }
  };

  const handleSubscribe = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setLoading(true);
    
    // If discount code is validated, use the discount apply endpoint
    if (validatedCode?.valid && discountCode) {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.post(`${API}/discount/apply`, { code: discountCode.toUpperCase() }, { headers });
        if (res.data.free) {
          toast.success(res.data.message);
          setValidatedCode(null);
          setDiscountCode('');
          window.location.reload();
        } else if (res.data.checkout_url) {
          window.location.href = res.data.checkout_url;
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Er ging iets mis');
      }
      setLoading(false);
      return;
    }
    
    const result = await subscribe(selectedPlan);
    setLoading(false);

    if (result.success && !result.redirect) {
      toast.success('Abonnement geactiveerd!');
      setValidatedCode(null);
      setDiscountCode('');
    } else if (!result.success) {
      toast.error(result.error);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Weet je zeker dat je je abonnement wilt opzeggen?')) return;

    setLoading(true);
    const result = await cancelSubscription();
    setLoading(false);

    if (result.success) {
      toast.success('Abonnement opgezegd');
    } else {
      toast.error(result.error);
    }
  };

  const features = [
    'Onbeperkt toegang tot alle verhalen',
    'Woord-highlighting tijdens voorlezen',
    'Nieuwe verhalen elke week',
    'Bedtijdmodus met sfeergeluiden',
    'Geen advertenties'
  ];

  return (
    <div className="min-h-screen pt-4 pb-36 md:pt-24 md:pb-8 px-4" data-testid="subscription-page">
      <SEO path="/subscription" />
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="mx-auto w-20 h-20 rounded-full bg-gold/20 flex items-center justify-center mb-4">
              <Crown className="w-10 h-10 text-gold" />
            </div>
            <h1 className="font-display text-4xl md:text-5xl text-foreground mb-3">
              Premium Lidmaatschap
            </h1>
            <p className="text-muted-foreground text-lg max-w-md mx-auto">
              Geef je (klein)kinderen onbeperkt toegang tot magische verhalen
            </p>
          </div>

          {/* Current Status */}
          {isAuthenticated && subscription && (
            <Card className="mb-8 border-2 border-gold/30">
              <CardHeader>
                <CardTitle className="font-display text-xl">Jouw Abonnement</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {isPremium ? (
                        <span className="px-3 py-1 rounded-full bg-gold/20 text-gold font-medium text-sm">
                          {isTrialing ? `Proefperiode (${trialDaysLeft} dagen over)` : 'Premium Actief'}
                        </span>
                      ) : (
                        <span className="px-3 py-1 rounded-full bg-muted text-muted-foreground font-medium text-sm">
                          {subscription.status === 'canceled' ? 'Opgezegd' : 'Gratis'}
                        </span>
                      )}
                    </div>
                    {isTrialing && subscription.trial_end_date && (
                      <p className="text-sm text-muted-foreground">
                        Proefperiode eindigt op {new Date(subscription.trial_end_date).toLocaleDateString('nl-NL')}
                      </p>
                    )}
                    {subscription.status === 'active' && subscription.subscription_end_date && (
                      <p className="text-sm text-muted-foreground">
                        Verlengt automatisch op {new Date(subscription.subscription_end_date).toLocaleDateString('nl-NL')}
                      </p>
                    )}
                  </div>
                  
                  {isPremium && (
                    <Button
                      variant="outline"
                      onClick={handleCancel}
                      disabled={loading}
                      className="text-destructive hover:text-destructive"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Opzeggen
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Trial Alert */}
          {isTrialing && trialDaysLeft <= 2 && (
            <Alert className="mb-8 border-gold/50 bg-gold/10">
              <AlertTriangle className="w-4 h-4 text-gold" />
              <AlertDescription className="text-foreground">
                Je proefperiode eindigt over {trialDaysLeft} {trialDaysLeft === 1 ? 'dag' : 'dagen'}. 
                Upgrade naar premium om toegang te behouden!
              </AlertDescription>
            </Alert>
          )}

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            {/* Free Plan */}
            <Card className="border-2 border-border">
              <CardHeader>
                <CardTitle className="font-display text-2xl">Gratis</CardTitle>
                <CardDescription>Probeer onze verhalen</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <span className="text-4xl font-bold">€0</span>
                  <span className="text-muted-foreground">/maand</span>
                </div>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>2 gratis verhalen</span>
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <X className="w-4 h-4 text-red-400" />
                    <span>Woord-highlighting tijdens voorlezen</span>
                  </li>
                  <li className="flex items-center gap-2 text-muted-foreground">
                    <X className="w-4 h-4 text-red-400" />
                    <span>Alle verhalen</span>
                  </li>
                </ul>
                <Button 
                  variant="outline" 
                  className="w-full"
                  disabled
                >
                  Huidige plan
                </Button>
              </CardContent>
            </Card>

            {/* Premium Plan */}
            <Card className="border-2 border-gold/50 bg-gradient-to-b from-gold/5 to-transparent relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-gold text-night-blue text-xs font-bold px-3 py-1 rounded-bl-lg">
                POPULAIR
              </div>
              <CardHeader>
                <CardTitle className="font-display text-2xl flex items-center gap-2">
                  <Crown className="w-6 h-6 text-gold" />
                  Premium
                </CardTitle>
                <CardDescription>Onbeperkt verhalen</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <span className="text-4xl font-bold">
                    {selectedPlan === 'yearly' ? '€29,90' : '€2,99'}
                  </span>
                  <span className="text-muted-foreground">
                    {selectedPlan === 'yearly' ? '/jaar' : '/maand'}
                  </span>
                  {selectedPlan === 'yearly' && (
                    <div className="text-xs text-green-600 font-bold mt-1" data-testid="yearly-savings">
                      ≈ €2,49/maand · je bespaart €5,98 per jaar
                    </div>
                  )}
                </div>

                {/* Plan toggle */}
                {!isPremium && !hideWebPurchaseUI && (
                  <div className="mb-6 grid grid-cols-2 gap-2 p-1 bg-muted/50 rounded-xl" data-testid="plan-toggle">
                    <button
                      data-testid="plan-monthly-btn"
                      onClick={() => setSelectedPlan('monthly')}
                      className={`py-2 px-3 rounded-lg text-sm font-bold transition-colors ${selectedPlan === 'monthly' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'}`}
                    >
                      Maandelijks
                    </button>
                    <button
                      data-testid="plan-yearly-btn"
                      onClick={() => setSelectedPlan('yearly')}
                      className={`relative py-2 px-3 rounded-lg text-sm font-bold transition-colors ${selectedPlan === 'yearly' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'}`}
                    >
                      Jaarlijks
                      <span className="absolute -top-2 -right-2 bg-green-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                        2 mnd gratis
                      </span>
                    </button>
                  </div>
                )}

                <ul className="space-y-3 mb-6">
                  {features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* Discount Code Section (hidden on iOS — Reader App compliance) */}
                {!isPremium && !hideWebPurchaseUI && (
                  <div className="mb-6 p-4 bg-muted/50 rounded-xl">
                    <label className="text-sm font-medium mb-2 block">
                      <Tag className="w-4 h-4 inline mr-1" />
                      Heb je een kortingscode?
                    </label>
                    <div className="flex gap-2">
                      <Input
                        data-testid="discount-code-input"
                        placeholder="KORTINGSCODE"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        className="font-mono uppercase"
                        onBlur={() => validateDiscountCode(discountCode)}
                      />
                      <Button
                        data-testid="validate-code-btn"
                        variant="outline"
                        onClick={() => validateDiscountCode(discountCode)}
                        disabled={validatingCode || !discountCode}
                      >
                        {validatingCode ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          'Check'
                        )}
                      </Button>
                    </div>
                    {validatedCode && (
                      <div className="mt-2 text-sm text-green-600">
                        <span className="flex items-center gap-1">
                          <Check className="w-4 h-4" />
                          {validatedCode.description}! 
                          Prijs: <span className="line-through text-muted-foreground ml-1">€{validatedCode.original_price?.toFixed(2)}</span>
                          <span className="font-bold text-green-600 ml-1">€{validatedCode.final_price?.toFixed(2)}</span>
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {!isPremium && !hideWebPurchaseUI && (
                  <div className="space-y-3">
                    {subscription?.trial_available && (
                      <Button
                        data-testid="start-trial-page-btn"
                        onClick={handleStartTrial}
                        disabled={loading || subLoading}
                        className="w-full rounded-full bg-gold hover:bg-gold/90 text-night-blue font-bold"
                      >
                        {loading ? (
                          <div className="w-5 h-5 border-2 border-night-blue/30 border-t-night-blue rounded-full animate-spin" />
                        ) : (
                          <>
                            <Clock className="w-4 h-4 mr-2" />
                            7 dagen gratis proberen
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      data-testid="subscribe-page-btn"
                      onClick={handleSubscribe}
                      disabled={loading || subLoading}
                      variant={subscription?.trial_available ? "outline" : "default"}
                      className={`w-full rounded-full ${!subscription?.trial_available ? 'bg-gold hover:bg-gold/90 text-night-blue font-bold' : ''}`}
                    >
                      {loading ? (
                        <div className="w-5 h-5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Nu abonneren
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {/* iOS Reader App notice — Apple Guideline 3.1.3 */}
                {!isPremium && hideWebPurchaseUI && (
                  <div className="mt-4 p-4 rounded-xl bg-muted/60 border border-border" data-testid="ios-reader-notice">
                    <p className="text-sm text-foreground font-medium mb-2">
                      Beheer je abonnement op onze website
                    </p>
                    <p className="text-xs text-muted-foreground mb-3">
                      Log daarna in deze app in met hetzelfde account om je verhalen te lezen.
                    </p>
                    <a
                      href="https://omasverhaaltjes.nl"
                      className="inline-flex items-center gap-2 text-sm text-gold hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="ios-manage-link"
                    >
                      Ga naar omasverhaaltjes.nl
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                )}

                {isPremium && (
                  <Button className="w-full" disabled>
                    <Check className="w-4 h-4 mr-2" />
                    Jouw huidige plan
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>

          {/* FAQ */}
          <div className="text-center">
            <h2 className="font-display text-2xl text-foreground mb-4">Veelgestelde vragen</h2>
            <div className="grid md:grid-cols-2 gap-4 text-left">
              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-bold mb-2">Hoe werkt de proefperiode?</h3>
                  <p className="text-sm text-muted-foreground">
                    Je krijgt 7 dagen volledige toegang tot alle premium functies. Na de proefperiode kun je kiezen om te abonneren of terug te gaan naar gratis.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-bold mb-2">Kan ik opzeggen?</h3>
                  <p className="text-sm text-muted-foreground">
                    Ja, je kunt elk moment opzeggen. Je houdt toegang tot het einde van je betaalperiode.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-bold mb-2">Welke betaalmethodes?</h3>
                  <p className="text-sm text-muted-foreground">
                    We accepteren iDEAL, creditcard en andere populaire betaalmethodes via Mollie.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-bold mb-2">Is het veilig voor kinderen?</h3>
                  <p className="text-sm text-muted-foreground">
                    Absoluut! Alle verhalen worden gecontroleerd op kindvriendelijke content voordat ze worden gepubliceerd.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Gift CTA */}
          <div className="mt-12 text-center p-8 rounded-2xl bg-gradient-to-r from-old-rose/10 to-gold/10 border border-old-rose/20">
            <Gift className="w-10 h-10 mx-auto mb-3 text-old-rose" />
            <h3 className="font-display text-2xl mb-2">Geef verhalen cadeau!</h3>
            <p className="text-muted-foreground text-sm mb-4 max-w-md mx-auto">
              Het mooiste cadeau voor een oma, opa of gezin. Kies uit 1 maand, 3 maanden of 1 jaar premium.
            </p>
            <Link to="/gift">
              <Button className="rounded-full bg-old-rose hover:bg-old-rose/90 text-night-blue font-bold px-8">
                <Gift className="w-4 h-4 mr-2" />
                Cadeau kopen
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Subscription;
